import { BellAlertIcon, BellSlashIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { apiRequest } from "src/async/apiUtils";
import FeedbackButton, { useFlash } from "src/components/ServerMonitoring/FeedbackButton";
import Section from "src/components/Shared/Containers/Section";
import Modal from "src/components/Shared/Modal";
import { classNames } from "src/helpers/classNames";
import { formatDateTime } from "src/helpers/serverMonitorHelper";

const PAUSE_OPTIONS = [
  { minutes: 30, label: "30 minutes" },
  { minutes: 60, label: "1 hour" },
  { minutes: 120, label: "2 hours" },
  { minutes: 240, label: "4 hours" },
];

// Crash alert emails: who receives them and pause during maintenance.
const AlertControls = ({ status, onChanged }) => {
  const [pauseOpen, setPauseOpen] = useState(false);
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(null);
  const [pauseError, setPauseError] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [flashKey, flash] = useFlash();

  useEffect(() => {
    if (pauseOpen) setPauseError(null);
  }, [pauseOpen]);

  const alerts = status?.alerts;
  if (!status?.configured || !alerts) return null;
  const paused = !!alerts.paused_until;

  // No toasts: success turns the button green in place, a failure is shown next to it (or inside the pause popup).
  const call = async (key, method, url, body = {}) => {
    try {
      setBusy(key);
      await apiRequest(method, url, { body });
      flash(key);
      onChanged();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || "The action failed." };
    } finally {
      setBusy(null);
    }
  };

  const resume = async () => {
    setResumeError(null);
    const result = await call("resume", "delete", "/server-monitor/alerts/pause");
    if (!result.ok) setResumeError(result.error);
  };

  const pause = async () => {
    setPauseError(null);
    const result = await call("pause", "post", "/server-monitor/alerts/pause", { minutes });
    if (result.ok) setPauseOpen(false);
    else setPauseError(result.error);
  };

  return (
    <Section shadow>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">Crash Alerts</h2>
          <span className={classNames("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold", paused ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700")}>
            {paused ? <BellSlashIcon className="h-3.5 w-3.5" /> : <BellAlertIcon className="h-3.5 w-3.5" />}
            {paused ? "Paused" : "Active"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AnimatePresence>
            {resumeError && (
              <motion.span
                key="resume-error"
                role="alert"
                initial={{ opacity: 0, x: 0 }}
                animate={{ opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex items-center gap-1 text-xs text-red-700">
                <XCircleIcon className="h-4 w-4" /> {resumeError}
              </motion.span>
            )}
          </AnimatePresence>
          {/* The confirmation keeps its place for a moment, even though the status has already switched. */}
          {flashKey === "pause" || flashKey === "resume" ? (
            <FeedbackButton
              version="secondary"
              type="button"
              done
              doneLabel={flashKey === "pause" ? "Alerts paused" : "Alerts resumed"}
            />
          ) : paused ? (
            <FeedbackButton
              version="secondary"
              type="button"
              disabled={busy === "resume"}
              loading={busy === "resume"}
              onClick={resume}>
              <BellAlertIcon className="h-4 w-4" /> Resume alerts
            </FeedbackButton>
          ) : (
            <FeedbackButton
              version="secondary"
              type="button"
              onClick={() => setPauseOpen(true)}>
              <BellSlashIcon className="h-4 w-4" /> Pause alerts
            </FeedbackButton>
          )}
        </div>
      </div>

      {paused && (
        <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Crash emails are paused until <b>{formatDateTime(alerts.paused_until, "MMM D, h:mm A")}</b> by {alerts.paused_by_name}. Crashes are still recorded below; alerts resume automatically.
        </p>
      )}

      <div className="mt-4 text-sm text-gray-600">
        One email per crash goes to{" "}
        {alerts.recipients?.length > 0 ? (
          <>
            <b>{alerts.recipients.join(", ")}</b> {alerts.recipients_source !== "env" && <span className="text-gray-400">(all active Super Admins)</span>}
          </>
        ) : (
          <span className="text-red-600">nobody - no alert email addresses are configured</span>
        )}
        . Recoveries are not emailed.
      </div>

      <Modal
        title="Pause crash alerts"
        isOpen={pauseOpen}
        onCancel={() => setPauseOpen(false)}
        onSuccess={pause}
        isLoading={busy === "pause"}
        defaultOptions={{ onSuccessButtonText: "Pause alerts", onSuccessLoaderVisible: true }}>
        <div className="space-y-3 pb-2 text-sm text-gray-700">
          {pauseError && (
            <motion.div
              role="alert"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
              transition={{ duration: 0.4 }}
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <XCircleIcon className="h-5 w-5 flex-shrink-0" />
              <div>{pauseError}</div>
            </motion.div>
          )}
          <p>Use this during planned maintenance. Crashes are still detected and recorded, but no email is sent for crashes during the pause - they are not sent later either. Alerts resume automatically.</p>
          <div className="grid grid-cols-2 gap-2">
            {PAUSE_OPTIONS.map((option) => (
              <label
                key={option.minutes}
                className={classNames("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2", minutes === option.minutes ? "border-highlightColor bg-gray-50" : "border-gray-200 hover:bg-gray-50")}>
                <input
                  type="radio"
                  name="pause-minutes"
                  className="text-highlightColor focus:ring-0"
                  checked={minutes === option.minutes}
                  onChange={() => setMinutes(option.minutes)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      </Modal>
    </Section>
  );
};

export default AlertControls;
